import { useEffect, useRef, useState } from 'react';
import { cyclesAPI } from '../../../api/cycles';

// How many closed windows per side "Past Windows" shows: the API's maximum (DWLF-444).
export const PAST_WINDOWS = 20;

// The closed cycle windows behind each side's current one, newest first, each with the `outcome` that
// closed it and its derived price band (DWLF-444). Fetched ONLY while the chart's Past Windows layer is
// shown, in its own request, so a failure here never touches the current window band.
//
// Returns { history: { low: [], high: [] } | null, loading, error }.
export const usePastCycleWindows = ({ symbol, timeframe, enabled = false }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    setHistory(null);
  }, [symbol, timeframe]);

  useEffect(() => {
    if (!enabled || !symbol || !timeframe) {
      setLoading(false);
      setError(null);
      return undefined;
    }
    const seq = ++requestSeqRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);
    cyclesAPI.getWindows({ symbol, timeframe, depth: true, history: PAST_WINDOWS })
      .then((data) => {
        if (cancelled || seq !== requestSeqRef.current) return;
        setHistory({ low: data?.low?.history ?? [], high: data?.high?.history ?? [] });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || seq !== requestSeqRef.current) return;
        setError(err?.message || 'past cycle windows fetch failed');
        setHistory(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe, enabled]);

  // In flight from the first render the layer is shown, before the effect has run: never "no windows" early.
  return { history, loading: loading || (enabled && !!symbol && !!timeframe && history === null && error === null), error };
};

export default usePastCycleWindows;
